import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiArrowLeft as ArrowLeft,
  FiEdit2 as Edit,
  FiSave as Save,
  FiX as X,
  FiTrash2 as Trash2,
  FiCopy as Copy,
  FiPlus as Plus,
  FiDownload as Download,
  FiFileText as FileText,
  FiMessageCircle as MessageCircle,
  FiCheckCircle as CheckCircle,
  FiAlertTriangle as AlertTriangle,
  FiPlay as Play,
  FiSearch as Search,
  FiGitMerge as GitMerge
} from 'react-icons/fi';
import { useDataset, useDatasetTestCases, useDatasetItems } from '../../../../hooks/useDataset';
import { cloneDataset } from '../../../../services/datasetsApi';
import { createTestCase, updateTestCase, deleteTestCase } from '../../../../services/testCasesApi';
import { createDatasetItem, updateDatasetItem, deleteDatasetItem, bulkDeleteDatasetItems } from '../../../../services/datasetItemsApi';
import TestCaseTable from '../components/datasets/TestCaseTable';
import TestCaseEditor from '../components/datasets/TestCaseEditor';
import DatasetItemsTable from '../components/datasets/DatasetItemsTable';
import DocumentItemEditor from '../components/datasets/DocumentItemEditor';
import QAPairItemEditor from '../components/datasets/QAPairItemEditor';
import VectorExportsTab from '../components/vector-exports/VectorExportsTab';
import RunEvaluationModal from '../../components/evaluations/RunEvaluationModal';
import ConfirmDialog from '../../../../components/common/ConfirmDialog';
import PromptDialog from '../../../../components/common/PromptDialog';
import Pagination from '../../../../components/common/Pagination';
import ImportJobStatus from '../components/datasets/ImportJobStatus';
import ProjectAssignmentSelect from '../../../../components/ProjectAssignmentSelect';
import WorkloadAssignmentSelect from '../../../../components/WorkloadAssignmentSelect';
import { assignGenAIOpsDatasetProject, assignGenAIOpsDatasetWorkload } from '../../../../services/aiOperationsApi';

const BACK_URL = '/ai-operations/registry?tab=datasets';

// Content type configuration with evaluability info
const CONTENT_TYPE_CONFIG = {
  test_cases: {
    label: 'Test Cases',
    Icon: CheckCircle,
    addLabel: 'Add Test Case',
    itemsLabel: 'Test Cases',
    evaluable: true,
    evaluabilityNote: 'Ready for evaluations with all scoring methods.',
    recommendedScoring: ['exact_match', 'contains', 'similarity', 'semantic_similarity', 'llm_judge']
  },
  documents: {
    label: 'Documents',
    Icon: FileText,
    addLabel: 'Add Document',
    itemsLabel: 'Documents',
    evaluable: true,
    evaluabilityNote: 'Documents can be evaluated using semantic scoring. For best results, use LLM Judge or Semantic Similarity scoring.',
    recommendedScoring: ['semantic_similarity', 'llm_judge'],
    vectorExportReady: true
  },
  qa_pairs: {
    label: 'Q&A Pairs',
    Icon: MessageCircle,
    addLabel: 'Add Q&A Pair',
    itemsLabel: 'Q&A Pairs',
    evaluable: true,
    evaluabilityNote: 'Ready for evaluations with all scoring methods.',
    recommendedScoring: ['exact_match', 'contains', 'similarity', 'semantic_similarity', 'llm_judge']
  },
  mixed: {
    label: 'Mixed Content',
    Icon: GitMerge,
    addLabel: 'Add Item',
    itemsLabel: 'Items',
    evaluable: true,
    evaluabilityNote: 'Mixed datasets can be evaluated. Items will be scored based on their type.',
    recommendedScoring: ['semantic_similarity', 'llm_judge']
  }
};

const DatasetDetailPage = () => {
  const { datasetId } = useParams();
  const navigate = useNavigate();
  const { dataset, loading, error, saving, update, remove, refresh } = useDataset(datasetId);

  // Search state with debounce
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchQuery]);

  // Page size state
  const [pageSize, setPageSize] = useState(25);

  // Get content type from dataset
  const contentType = dataset?.content_type || 'test_cases';
  const config = (CONTENT_TYPE_CONFIG as any)[contentType] || CONTENT_TYPE_CONFIG.test_cases;

  // For test_cases content type
  const {
    testCases,
    loading: testCasesLoading,
    refresh: refreshTestCases,
    page: testCasePage,
    setPage: setTestCasePage,
    totalPages: testCaseTotalPages
  } = useDatasetTestCases(datasetId, { searchTerm: debouncedSearch, pageSize });

  // For other content types (documents, qa_pairs, mixed)
  const {
    items: datasetItems,
    loading: itemsLoading,
    refresh: refreshItems,
    page: itemsPage,
    setPage: setItemsPage,
    totalPages: itemsTotalPages
  } = useDatasetItems(datasetId, { search: debouncedSearch, pageSize });

  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<Record<string, any>>({});

  // Test case modal state
  const [showTestCaseModal, setShowTestCaseModal] = useState(false);
  const [selectedTestCase, setSelectedTestCase] = useState<Record<string, any> | null>(null);
  const [savingTestCase, setSavingTestCase] = useState(false);

  // Document modal state
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Record<string, any> | null>(null);
  const [savingDocument, setSavingDocument] = useState(false);

  // Q&A Pair modal state
  const [showQAModal, setShowQAModal] = useState(false);
  const [selectedQA, setSelectedQA] = useState<Record<string, any> | null>(null);
  const [savingQA, setSavingQA] = useState(false);

  // Confirm/Prompt dialog state
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; bulk?: boolean; count?: number }>({ open: false });
  const [itemDeleteConfirm, setItemDeleteConfirm] = useState<{ open: boolean; item: Record<string, any> | null; type: string | null }>({ open: false, item: null, type: null });
  const [clonePrompt, setClonePrompt] = useState(false);
  const [showEvalModal, setShowEvalModal] = useState(false);

  // Bulk selection state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Initialize edited data when dataset loads
  useEffect(() => {
    if (dataset && !isEditing) {
      setEditedData({
        name: dataset.name,
        description: dataset.description || '',
        tags: dataset.tags || []
      });
    }
  }, [dataset, isEditing]);

  // Clear selection when page/search changes
  useEffect(() => {
    setSelectedItems(new Set());
  }, [testCasePage, itemsPage, debouncedSearch]);

  // Dataset handlers
  const handleSave = async () => {
    try {
      await update(editedData);
      setIsEditing(false);
      toast.success('Dataset saved');
    } catch (err: unknown) {
      toast.error(`Error saving dataset: ${(err instanceof Error ? err.message : String(err))}`);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (dataset) {
      setEditedData({
        name: dataset.name,
        description: dataset.description || '',
        tags: dataset.tags || []
      });
    }
  };

  const handleDelete = () => {
    setDeleteConfirm({ open: true });
  };

  const confirmDelete = async () => {
    setDeleteConfirm({ open: false });
    try {
      await remove();
      toast.success('Dataset deleted');
      navigate(BACK_URL);
    } catch (err: unknown) {
      toast.error(`Error deleting dataset: ${(err instanceof Error ? err.message : String(err))}`);
    }
  };

  const handleClone = () => {
    setClonePrompt(true);
  };

  const confirmClone = async (newName: string) => {
    setClonePrompt(false);
    if (!newName) return;
    try {
      const cloned = await cloneDataset(datasetId ?? '', newName);
      toast.success('Dataset cloned');
      navigate(`/ai-operations/datasets/${cloned.id}`);
    } catch (err: unknown) {
      toast.error(`Error cloning dataset: ${(err instanceof Error ? err.message : String(err))}`);
    }
  };

  const handleExport = () => {
    if (!dataset) return;

    const items = contentType === 'test_cases' ? testCases : datasetItems;
    const exportData = {
      name: dataset.name,
      description: dataset.description,
      content_type: contentType,
      tags: dataset.tags,
      version: dataset.version,
      items: items
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${dataset.name.replaceAll(/\s+/g, '-').toLowerCase()}-dataset.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Add item handler - opens the right modal based on content type
  const handleAddItem = (type: string | null = null) => {
    const itemType = type || contentType;

    switch (itemType) {
      case 'test_cases':
        setSelectedTestCase(null);
        setShowTestCaseModal(true);
        break;
      case 'documents':
      case 'document':
        setSelectedDocument(null);
        setShowDocumentModal(true);
        break;
      case 'qa_pairs':
      case 'qa_pair':
        setSelectedQA(null);
        setShowQAModal(true);
        break;
      case 'mixed':
        break;
      default:
        setSelectedTestCase(null);
        setShowTestCaseModal(true);
    }
  };

  // Test Case handlers
  const handleEditTestCase = (testCase: Record<string, any>) => {
    setSelectedTestCase(testCase);
    setShowTestCaseModal(true);
  };

  const handleSaveTestCase = async (testCaseData: Record<string, any>) => {
    setSavingTestCase(true);
    try {
      if (selectedTestCase) {
        await updateTestCase(selectedTestCase.id, testCaseData);
      } else {
        await createTestCase(datasetId ?? '', testCaseData);
      }
      setShowTestCaseModal(false);
      setSelectedTestCase(null);
      refreshTestCases();
      refresh();
      toast.success(selectedTestCase ? 'Test case updated' : 'Test case created');
    } catch (err: unknown) {
      console.error('Error saving test case:', err);
      toast.error(`Error: ${(err instanceof Error ? err.message : String(err))}`);
    } finally {
      setSavingTestCase(false);
    }
  };

  const handleDeleteTestCase = (testCase: Record<string, any>) => {
    setItemDeleteConfirm({ open: true, item: testCase, type: 'test_case' });
  };

  const confirmItemDelete = async () => {
    const { item, type } = itemDeleteConfirm;
    setItemDeleteConfirm({ open: false, item: null, type: null });
    try {
      if (type === 'test_case') {
        await deleteTestCase(item!.id);
        refreshTestCases();
      } else {
        await deleteDatasetItem(datasetId ?? '', item!.id);
        refreshItems();
      }
      refresh();
      toast.success('Item deleted');
    } catch (err: unknown) {
      toast.error(`Error: ${(err instanceof Error ? err.message : String(err))}`);
    }
  };

  // Document handlers
  const handleEditDocument = (item: Record<string, any>) => {
    setSelectedDocument(item);
    setShowDocumentModal(true);
  };

  const handleSaveDocument = async (itemData: Record<string, any>) => {
    setSavingDocument(true);
    try {
      if (selectedDocument) {
        await updateDatasetItem(datasetId ?? '', selectedDocument.id, itemData);
      } else {
        await createDatasetItem(datasetId ?? '', itemData);
      }
      setShowDocumentModal(false);
      setSelectedDocument(null);
      refreshItems();
      refresh();
      toast.success(selectedDocument ? 'Document updated' : 'Document created');
    } catch (err: unknown) {
      console.error('Error saving document:', err);
      toast.error(`Error: ${(err instanceof Error ? err.message : String(err))}`);
    } finally {
      setSavingDocument(false);
    }
  };

  // Q&A Pair handlers
  const handleEditQA = (item: Record<string, any>) => {
    setSelectedQA(item);
    setShowQAModal(true);
  };

  const handleSaveQA = async (itemData: Record<string, any>) => {
    setSavingQA(true);
    try {
      if (selectedQA) {
        await updateDatasetItem(datasetId ?? '', selectedQA.id, itemData);
      } else {
        await createDatasetItem(datasetId ?? '', itemData);
      }
      setShowQAModal(false);
      setSelectedQA(null);
      refreshItems();
      refresh();
      toast.success(selectedQA ? 'Q&A pair updated' : 'Q&A pair created');
    } catch (err: unknown) {
      console.error('Error saving Q&A pair:', err);
      toast.error(`Error: ${(err instanceof Error ? err.message : String(err))}`);
    } finally {
      setSavingQA(false);
    }
  };

  // Generic item handlers for DatasetItemsTable
  const handleEditItem = (item: Record<string, any>) => {
    if (item.item_type === 'document') {
      handleEditDocument(item);
    } else if (item.item_type === 'qa_pair') {
      handleEditQA(item);
    }
  };

  const handleDeleteItem = (item: Record<string, any>) => {
    setItemDeleteConfirm({ open: true, item, type: 'dataset_item' });
  };

  // Bulk delete handler
  const handleBulkDelete = () => {
    if (selectedItems.size === 0) return;
    setDeleteConfirm({
      open: true,
      bulk: true,
      count: selectedItems.size,
    });
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedItems);
    setDeleteConfirm({ open: false });
    try {
      if (contentType === 'test_cases') {
        // Delete test cases one by one (no bulk endpoint for test cases)
        await Promise.all(ids.map(id => deleteTestCase(id)));
        refreshTestCases();
      } else {
        await bulkDeleteDatasetItems(datasetId ?? '', ids);
        refreshItems();
      }
      refresh();
      setSelectedItems(new Set());
      toast.success(`Deleted ${ids.length} items`);
    } catch (err: unknown) {
      toast.error(`Error: ${(err instanceof Error ? err.message : String(err))}`);
    }
  };

  // Selection handlers
  const handleSelectionChange = useCallback((newSelection: Set<string>) => {
    setSelectedItems(newSelection);
  }, []);

  // Get item count based on content type
  const getItemCount = () => {
    if (contentType === 'test_cases') {
      return dataset?.test_case_count || 0;
    }
    return dataset?.item_count || 0;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !dataset) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            {error || 'Dataset not found'}
          </p>
          <button
            onClick={() => navigate(BACK_URL)}
            className="mt-4 text-red-600 dark:text-red-400 hover:underline"
          >
            Back to Datasets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(BACK_URL)}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Datasets
        </button>

        <div className="flex justify-between items-start">
          <div>
            {isEditing ? (
              <input
                type="text"
                value={editedData.name}
                onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
                className="text-3xl font-bold text-gray-900 dark:text-white bg-transparent border-b-2 border-blue-500 focus:outline-none"
              />
            ) : (
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white transition-colors">
                  {dataset.name}
                </h1>
                <span className="inline-flex items-center gap-1 px-3 py-1 text-sm rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                  <config.Icon className="w-4 h-4" />
                  {config.label}
                </span>
              </div>
            )}
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 transition-colors">
              Version {dataset.version} | {getItemCount()} {config.itemsLabel.toLowerCase()}
            </p>
          </div>

          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  disabled={saving}
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  disabled={saving}
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={handleClone}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Clone
                </button>
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Export
                </button>
                <button
                  onClick={() => setShowEvalModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Play className="w-4 h-4" />
                  Run Evaluation
                </button>
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-2 px-4 py-2 border border-red-300 dark:border-red-600 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 transition-colors">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Description</h2>
        {isEditing ? (
          <textarea
            value={editedData.description}
            onChange={(e) => setEditedData({ ...editedData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            rows={3}
            placeholder="Add a description for this dataset"
          />
        ) : (
          <p className="text-gray-600 dark:text-gray-400">
            {dataset.description || 'No description provided'}
          </p>
        )}
      </div>

      {/* Tags */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 transition-colors">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Tags</h2>
        {isEditing ? (
          <input
            type="text"
            value={Array.isArray(editedData.tags) ? editedData.tags.join(', ') : ''}
            onChange={(e) => setEditedData({
              ...editedData,
              tags: e.target.value.split(',').map(t => t.trim()).filter(t => t)
            })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., support, billing, english"
          />
        ) : (
          <div className="flex gap-2 flex-wrap">
            {(dataset.tags?.length ?? 0) > 0 ? (
              dataset.tags!.map((tag: string) => (
                <span
                  key={tag}
                  className="px-3 py-1 text-sm rounded-full bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                >
                  {tag}
                </span>
              ))
            ) : (
              <span className="text-gray-400">No tags</span>
            )}
          </div>
        )}
      </div>

      {/* Project & Workload Assignment */}
      <div className="mb-8 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 transition-colors">
        <div className="flex items-start gap-8">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Project</h2>
            <ProjectAssignmentSelect
              currentProjectId={dataset.project_id}
              onAssign={async (projectId) => {
                await assignGenAIOpsDatasetProject(dataset.id, projectId);
                refresh();
              }}
            />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Workload</h2>
            <WorkloadAssignmentSelect
              currentWorkloadId={dataset.workload_id}
              onAssign={async (workloadId) => {
                await assignGenAIOpsDatasetWorkload(dataset.id, workloadId);
                refresh();
              }}
            />
          </div>
        </div>
      </div>

      {/* Import Job Status */}
      <ImportJobStatus datasetId={datasetId ?? ''} />

      {/* Evaluability Info Box */}
      <div className="mb-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          {config.evaluable ? (
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-medium text-gray-900 dark:text-white">
                Evaluation Ready
              </h4>
              {contentType === 'documents' && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300">
                  RAG Ready
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {config.evaluabilityNote}
            </p>
            {config.recommendedScoring && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-gray-500 dark:text-gray-400">Recommended scoring:</span>
                {config.recommendedScoring.map((method: string) => (
                  <span key={method} className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                    {method.replaceAll('_', ' ')}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Items Section - Conditional based on content type */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{config.itemsLabel}</h2>

          <div className="flex items-center gap-3">
            {/* Search input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${config.itemsLabel.toLowerCase()}...`}
                className="pl-9 pr-3 py-2 w-64 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Add button(s) */}
            {contentType === 'mixed' ? (
              <div className="flex gap-2">
                <button
                  onClick={() => handleAddItem('document')}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  Add Document
                </button>
                <button
                  onClick={() => handleAddItem('qa_pair')}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  Add Q&A
                </button>
              </div>
            ) : (
              <button
                onClick={() => handleAddItem()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                {config.addLabel}
              </button>
            )}
          </div>
        </div>

        {/* Bulk selection action bar */}
        {selectedItems.size > 0 && (
          <div className="mb-4 flex items-center gap-3 px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {selectedItems.size} item{selectedItems.size === 1 ? '' : 's'} selected
            </span>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-lg transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete Selected
            </button>
            <button
              onClick={() => setSelectedItems(new Set())}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 ml-auto"
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Render the appropriate table based on content type */}
        {contentType === 'test_cases' ? (
          <>
            <TestCaseTable
              testCases={testCases as any}
              loading={testCasesLoading}
              onEdit={handleEditTestCase}
              onDelete={handleDeleteTestCase}
              selectable
              selectedIds={selectedItems}
              onSelectionChange={handleSelectionChange}
            />

            {/* Pagination for test cases */}
            <div className="mt-6">
              <Pagination
                currentPage={testCasePage}
                totalPages={testCaseTotalPages}
                onPageChange={setTestCasePage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
            </div>
          </>
        ) : (
          <>
            <DatasetItemsTable
              items={datasetItems as any}
              loading={itemsLoading}
              contentType={contentType}
              onEdit={handleEditItem}
              onDelete={handleDeleteItem}
              selectable
              selectedIds={selectedItems}
              onSelectionChange={handleSelectionChange}
            />

            {/* Pagination for dataset items */}
            <div className="mt-6">
              <Pagination
                currentPage={itemsPage}
                totalPages={itemsTotalPages}
                onPageChange={setItemsPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
            </div>
          </>
        )}
      </div>

      {/* Vector Exports Section */}
      <div className="mb-8">
        <VectorExportsTab datasetId={datasetId ?? ''} datasetName={dataset?.name} />
      </div>

      {/* Modals */}

      {/* Test Case Editor Modal */}
      <TestCaseEditor
        isOpen={showTestCaseModal}
        onClose={() => {
          setShowTestCaseModal(false);
          setSelectedTestCase(null);
        }}
        onSave={handleSaveTestCase}
        testCase={selectedTestCase}
        saving={savingTestCase}
      />

      {/* Document Editor Modal */}
      <DocumentItemEditor
        isOpen={showDocumentModal}
        onClose={() => {
          setShowDocumentModal(false);
          setSelectedDocument(null);
        }}
        onSave={handleSaveDocument}
        item={selectedDocument}
        saving={savingDocument}
      />

      {/* Q&A Pair Editor Modal */}
      <QAPairItemEditor
        isOpen={showQAModal}
        onClose={() => {
          setShowQAModal(false);
          setSelectedQA(null);
        }}
        onSave={handleSaveQA}
        item={selectedQA}
        saving={savingQA}
      />

      {/* Run Evaluation Modal */}
      <RunEvaluationModal
        isOpen={showEvalModal}
        onClose={() => setShowEvalModal(false)}
        onSuccess={() => {
          setShowEvalModal(false);
          toast.success('Evaluation started');
        }}
        initialDatasetId={datasetId}
      />

      {/* Delete dataset confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.open && !deleteConfirm.bulk}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ open: false })}
        title="Delete Dataset"
        message={`Are you sure you want to delete "${dataset?.name}"? This will also delete all items. This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Bulk delete confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.open && !!deleteConfirm.bulk}
        onConfirm={confirmBulkDelete}
        onCancel={() => setDeleteConfirm({ open: false })}
        title="Delete Selected Items"
        message={`Are you sure you want to delete ${deleteConfirm.count || 0} selected item${(deleteConfirm.count || 0) === 1 ? '' : 's'}? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Delete single item confirmation */}
      <ConfirmDialog
        isOpen={itemDeleteConfirm.open}
        onConfirm={confirmItemDelete}
        onCancel={() => setItemDeleteConfirm({ open: false, item: null, type: null })}
        title="Delete Item"
        message={`Delete "${itemDeleteConfirm.item?.name || itemDeleteConfirm.item?.type_specific_data?.title || 'this item'}"?`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Clone prompt dialog */}
      <PromptDialog
        isOpen={clonePrompt}
        onSubmit={confirmClone}
        onCancel={() => setClonePrompt(false)}
        title="Clone Dataset"
        message="Enter a name for the cloned dataset:"
        defaultValue={`${dataset?.name} (Copy)`}
        submitText="Clone"
      />
    </div>
  );
};

export default DatasetDetailPage;
